import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Route: /corporate
// Add to App.jsx or router file:
//   import CorporateServicesPage from "./pages/CorporateServicesPage";
//   <Route path="/corporate" element={<CorporateServicesPage />} />
// Local dev: http://localhost:3000/corporate

const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g400: "#28b566",
  g200: "#a7d9bc", g100: "#d6f0e3", g50: "#eef8f3",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268",
  n500: "#6b7280", n400: "#8a93a8", n300: "#b0b8cc", n200: "#d1d5e0",
  n150: "#e4e7ef", n100: "#eef0f5", n50: "#f7f8fa", white: "#ffffff",
  amber: "#d97706", amberBg: "#fffbeb", amberBorder: "#fde68a",
  blue: "#2563eb", blueBg: "#eff6ff", blueBorder: "#bfdbfe",
  serif: "'Instrument Serif', Georgia, serif",
  sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

const Icon = ({ name, size = 20, color = "currentColor", strokeWidth = 1.6 }) => {
  const paths = {
    ArrowLeft:       <><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></>,
    ArrowRight:      <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
    CheckCircle:     <><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></>,
    Building2:       <><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 0-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/></>,
    Briefcase:       <><rect width="20" height="14" x="2" y="7" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></>,
    Users:           <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    Droplets:        <><path d="M7 16.3c2.2 0 4-1.83 4-4.05 0-1.16-.57-2.26-1.71-3.19S7.29 6.75 7 5.3c-.29 1.45-1.14 2.84-2.29 3.76S3 11.1 3 12.25c0 2.22 1.8 4.05 4 4.05z"/><path d="M12.56 6.6A10.97 10.97 0 0 0 14 3.02c.5 2.5 2 4.9 4 6.5s3 3.5 3 5.5a6.98 6.98 0 0 1-11.91 4.97"/></>,
    Plug:            <><path d="M12 22v-5"/><path d="M9 8V2"/><path d="M15 8V2"/><path d="M18 8H6a2 2 0 0 0-2 2v2a7 7 0 1 0 14 0v-2a2 2 0 0 0-2-2Z"/></>,
    Leaf:            <><path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z"/><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/></>,
    Camera:          <><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></>,
    Truck:           <><path d="M5 17H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v3"/><rect width="7" height="7" x="14" y="10" rx="1"/><circle cx="7.5" cy="17.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></>,
    Wrench:          <><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></>,
    Repeat:          <><path d="m17 2 4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><path d="m7 22-4-4 4-4"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></>,
    LayoutDashboard: <><rect width="7" height="9" x="3" y="3" rx="1"/><rect width="7" height="5" x="14" y="3" rx="1"/><rect width="7" height="9" x="14" y="12" rx="1"/><rect width="7" height="5" x="3" y="16" rx="1"/></>,
    FileText:        <><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></>,
    ShieldCheck:     <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></>,
    BadgeCheck:      <><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m9 12 2 2 4-4"/></>,
    Star:            <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>,
    Clock:           <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    Phone:           <><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.5a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.34 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.09a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></>,
    TrendingUp:      <><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></>,
    BarChart2:       <><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></>,
    CreditCard:      <><rect width="20" height="14" x="2" y="5" rx="2"/><path d="M2 10h20"/></>,
    Calendar:        <><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></>,
    Zap:             <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>,
    Lock:            <><rect width="18" height="11" x="3" y="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>,
    ChevronDown:     <><path d="m6 9 6 6 6-6"/></>,
    ChevronUp:       <><path d="m18 15-6-6-6 6"/></>,
    MapPin:          <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/></>,
    Home:            <><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>,
    User:            <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>,
    Key:             <><circle cx="7.5" cy="15.5" r="5.5"/><path d="m21 2-9.6 9.6"/><path d="m15.5 7.5 3 3L22 7l-3-3"/></>,
    Award:           <><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* ── Services offered ── */
const SERVICES = [
  {
    icon: "Home",
    label: "Office cleaning and janitorial",
    desc: "Daily, weekly, or fortnightly scheduled cleaning for offices, reception areas, washrooms, and common spaces. Briefing your cleaner is a one-time task. Everything after that runs automatically.",
    tags: ["Daily or weekly schedules", "Washrooms and common areas", "Before-hours availability"],
  },
  {
    icon: "Plug",
    label: "Electrical and plumbing maintenance",
    desc: "Lighting, sockets, circuit breakers, leaking fixtures, drain blockages. Book a verified electrician or plumber on demand or on a scheduled basis to keep your office functional without downtime.",
    tags: ["On-demand or scheduled", "Certified electricians", "Emergency callouts available"],
  },
  {
    icon: "Leaf",
    label: "Landscaping and outdoor upkeep",
    desc: "Compound maintenance, hedging, lawn care, and outdoor cleaning for offices with gardens, car parks, or gated compounds. Set a recurring schedule and the same Pro returns on time every cycle.",
    tags: ["Recurring outdoor maintenance", "Compound and car park care", "Consistent Pro assignment"],
  },
  {
    icon: "Camera",
    label: "Security system installation and servicing",
    desc: "CCTV installation, access control wiring, intercom setup, and ongoing system maintenance. Business-tier JiraniPros handling security work carry relevant certifications and can sign NDAs on request.",
    tags: ["CCTV and access control", "NDA-ready professionals", "Ongoing servicing available"],
  },
  {
    icon: "Truck",
    label: "Moving and relocation support",
    desc: "Office relocations, floor moves, furniture rearrangements, and equipment transport handled by vetted moving teams. Book as far in advance as needed or call on short notice for urgent moves.",
    tags: ["Full office relocations", "Internal floor moves", "Short-notice bookings accepted"],
  },
  {
    icon: "Wrench",
    label: "General repairs and handyman work",
    desc: "Shelving, furniture assembly, partition walls, ceiling tiles, door handles, painting touch-ups. The category of work that always accumulates and never gets done. Book it in one session.",
    tags: ["Furniture and fixtures", "Painting and touch-ups", "Batch multiple jobs in one visit"],
  },
];

/* ── Personas ── */
const PERSONAS = [
  {
    role: "Facilities and operations manager",
    icon: "Briefcase",
    headline: "You need things to work. Not more admin.",
    points: [
      "Book any service from your dashboard without calling around for quotes",
      "Track every active job in real time, know who is on site and when they arrived",
      "Set recurring schedules once and let them run without manual rebooking",
      "Receive completion reports with timestamps and photos after every job",
      "Escalate issues directly to your dedicated account support contact",
    ],
  },
  {
    role: "Finance lead or CFO",
    icon: "BarChart2",
    headline: "One invoice. Full visibility. No surprises.",
    points: [
      "Receive a single itemised invoice at month end covering every job across all locations",
      "Every line item shows the date, service type, JiraniPro name, and amount",
      "Download invoices as PDF for your accounts team or auditor",
      "Set spend approval thresholds so high-cost jobs require sign-off before booking",
      "Quarterly reports break down spend by service category, location, and frequency",
    ],
  },
  {
    role: "CEO or director of a growing company",
    icon: "TrendingUp",
    headline: "Your office should run itself. This is how.",
    points: [
      "Replace ad hoc contractor relationships with one reliable, accountable platform",
      "No more operational decisions landing on your desk because a cleaner cancelled",
      "Your team books and tracks independently without needing your involvement",
      "All work is documented and invoiced, ready for board reporting or due diligence",
      "Scale the account as you grow: add locations, team members, and services at any time",
    ],
  },
];

/* ── How it works ── */
const HOW_IT_WORKS = [
  {
    num: "01", icon: "Briefcase",
    title: "Create your corporate account",
    desc: "Sign up under JiraniApp for Business and select the corporate account type. Add your company name, business address, and billing contact. Your 14-day free trial begins immediately with full access to the Business dashboard.",
    chips: ["Under 5 minutes", "14-day free trial", "Full dashboard access from day one"],
  },
  {
    num: "02", icon: "Users",
    title: "Add your team and set permissions",
    desc: "Invite the people who will be booking and tracking services. Assign roles: your facilities manager can book and approve, your finance lead can view invoices and reports, junior staff can submit requests for approval. Everyone sees what is relevant to them.",
    chips: ["Custom roles per team member", "Approval workflows available", "Finance-only access for accounts team"],
  },
  {
    num: "03", icon: "Repeat",
    title: "Set up your recurring services",
    desc: "Go through your standard maintenance needs and schedule them to repeat automatically. Daily office cleaning, weekly compound maintenance, monthly electrical checks. Assign your preferred JiraniPro to each schedule. Confirm once. They run on their own.",
    chips: ["Daily, weekly, or monthly cadence", "Preferred Pro locked per schedule", "Automatic Pro notification before each visit"],
  },
  {
    num: "04", icon: "Zap",
    title: "Book on-demand for everything else",
    desc: "For work that does not repeat, book directly from the dashboard. Select the service, date, time, location, and any special instructions. A matched business-tier JiraniPro is confirmed within the hour for most service types. Emergency callouts are handled as priority.",
    chips: ["Confirmed within the hour", "Emergency callout priority", "Same-day availability for most services"],
  },
  {
    num: "05", icon: "LayoutDashboard",
    title: "Track and manage from one screen",
    desc: "Your dashboard shows every active, upcoming, and completed job. Live status updates as the JiraniPro checks in, completes work, and uploads post-job documentation. Filter by service type, location, team member, or date. Nothing is lost and nothing needs chasing.",
    chips: ["Live job status", "Post-job photos and notes", "Filter by service, date, or location"],
  },
  {
    num: "06", icon: "FileText",
    title: "Receive one invoice at month end",
    desc: "One consolidated invoice arrives at the end of each billing period covering every job across every location. Itemised by date, service type, JiraniPro, and amount. PDF ready for your accounts. Your finance team has everything they need without asking you for it.",
    chips: ["Single monthly invoice", "Itemised by job and location", "PDF download for accounts"],
  },
];

/* ── Stats ── */
const STATS = [
  { value: "94%", label: "of business clients report a measurable reduction in maintenance downtime within the first 60 days" },
  { value: "1 invoice", label: "replaces an average of 14 separate payments per month for offices that previously used ad hoc contractors" },
  { value: "< 1 hour", label: "average confirmation time for on-demand bookings on the Business plan during office hours" },
  { value: "4.8 / 5", label: "average JiraniPro rating across all completed corporate bookings on the platform" },
];

/* ── FAQs ── */
const FAQS = [
  {
    q: "How is JiraniApp for corporates different from booking as an individual?",
    a: "A corporate account gives you a multi-user dashboard, consolidated monthly invoicing, recurring job scheduling, preferred Pro assignment, spend approval workflows, and access to business-tier verified JiraniPros with higher rating thresholds and commercial work experience. Individual accounts have none of these. Everything in a corporate account is designed around teams managing ongoing operational needs across one or more locations.",
  },
  {
    q: "Can different team members book different service types?",
    a: "Yes. On Enterprise accounts, permissions can be scoped to specific service categories or locations. On Business plan accounts, team members with booking access can book any service type available under the account. If you need tighter controls, for example a facilities manager who can book cleaning but not security installations, that level of permission scoping is available on Enterprise.",
  },
  {
    q: "How do spend approval workflows function?",
    a: "You can set a spend threshold in your account settings. Any booking above that amount triggers an approval request to the designated approver before confirmation is sent to the JiraniPro. The approver receives a notification, reviews the job details and cost, and approves or declines with one tap. The booking is only confirmed once approval is granted. This is available on Business and Enterprise plans.",
  },
  {
    q: "What makes business-tier JiraniPros different from standard ones?",
    a: "Business-tier JiraniPros have maintained a rating of 4.5 stars or higher across a minimum of 30 completed jobs, have a low cancellation and no-show rate, and have been reviewed by our team as suitable for commercial environments. For specific roles like security installation, additional certification is verified. Business-tier Pros are only matched to Business and Enterprise plan accounts.",
  },
  {
    q: "Can JiraniPros sign NDAs for sensitive corporate environments?",
    a: "Yes. JiraniPros working security installations or in environments requiring confidentiality can sign NDAs arranged through your account manager. This applies to Enterprise accounts by default and is available on request for Business plan accounts. Your account manager will coordinate the documentation before the JiraniPro attends site.",
  },
  {
    q: "What happens if a recurring job is missed or a Pro cancels?",
    a: "If a JiraniPro assigned to a recurring schedule is unable to attend, your account contact is notified at least 48 hours in advance for planned absences and immediately for last-minute changes. A qualified replacement from our business-tier pool is arranged and presented to you for approval. Business accounts are prioritised in the rematch queue. Missed visits that are the Pro's fault are not charged.",
  },
  {
    q: "How are disputes handled for business accounts?",
    a: "Business accounts have a dedicated support contact who handles disputes directly rather than routing them through the standard support queue. If a job was incomplete or not done to standard, raise it within 48 hours. Your contact will review the job record, photos, timestamps, and messages from both sides and work toward resolution, including a partial or full credit on your next invoice where warranted.",
  },
  {
    q: "Is there a minimum contract term for corporate accounts?",
    a: "No. Business plans are billed monthly with no minimum commitment. You can cancel before the next billing cycle with no penalty. Enterprise accounts may have custom terms depending on the arrangement, but these are always agreed in advance and documented in writing. We do not lock corporate clients into annual contracts without them explicitly choosing that arrangement.",
  },
];

/* ── Persona card ── */
function PersonaCard({ persona, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      flex: "1 1 200px", padding: "20px", borderRadius: 14, textAlign: "left",
      border: `1.5px solid ${active ? T.g500 : T.n150}`,
      background: active ? T.g500 : T.white,
      cursor: "pointer", fontFamily: T.sans, transition: "all .2s",
    }}>
      <div style={{ width: 36, height: 36, borderRadius: 10, background: active ? T.g400 : T.n50, border: `1px solid ${active ? T.g400 : T.n150}`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12, transition: "all .2s" }}>
        <Icon name={persona.icon} size={16} color={active ? T.white : T.n400} strokeWidth={1.7} />
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: active ? T.white : T.n900, lineHeight: 1.4, letterSpacing: "-0.01em" }}>{persona.role}</div>
    </button>
  );
}

/* ── Service card ── */
function ServiceCard({ icon, label, desc, tags }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: "22px", borderRadius: 16,
        border: `1.5px solid ${hovered ? T.g200 : T.n150}`,
        background: hovered ? T.g50 : T.white,
        transition: "all .18s", display: "flex", flexDirection: "column", gap: 12,
      }}>
      <div style={{ width: 42, height: 42, borderRadius: 11, background: hovered ? T.g100 : T.n50, border: `1px solid ${hovered ? T.g200 : T.n150}`, display: "flex", alignItems: "center", justifyContent: "center", transition: "all .18s" }}>
        <Icon name={icon} size={18} color={hovered ? T.g600 : T.n400} strokeWidth={1.6} />
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.n900, marginBottom: 7, letterSpacing: "-0.01em" }}>{label}</div>
        <p style={{ fontSize: 13, color: T.n600, lineHeight: 1.75, margin: "0 0 12px" }}>{desc}</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {tags.map((t, i) => (
            <span key={i} style={{ fontSize: 11, color: hovered ? T.g700 : T.n500, background: hovered ? T.white : T.n50, border: `1px solid ${hovered ? T.g100 : T.n150}`, borderRadius: 99, padding: "3px 10px", fontWeight: 500, fontFamily: T.sans, transition: "all .18s" }}>{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── How it works step ── */
function WorkStep({ step, isLast }) {
  return (
    <div style={{ display: "flex", gap: 20, position: "relative" }}>
      {!isLast && (
        <div style={{ position: "absolute", left: 23, top: 52, bottom: -16, width: 1, background: `linear-gradient(to bottom, ${T.g200}, ${T.n100})` }} />
      )}
      <div style={{ width: 48, height: 48, borderRadius: 14, background: T.g50, border: `1.5px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, zIndex: 1 }}>
        <Icon name={step.icon} size={20} color={T.g500} strokeWidth={1.6} />
      </div>
      <div style={{ flex: 1, paddingBottom: isLast ? 0 : 44 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: T.g500, letterSpacing: "0.1em", marginBottom: 5 }}>STEP {step.num}</div>
        <h3 style={{ fontFamily: T.serif, fontSize: "clamp(18px,2.8vw,22px)", fontWeight: 400, color: T.n900, margin: "0 0 10px", letterSpacing: "-0.02em", lineHeight: 1.2 }}>{step.title}</h3>
        <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.78, margin: "0 0 14px" }}>{step.desc}</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {step.chips.map((c, i) => (
            <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: T.g700, background: T.g50, border: `1px solid ${T.g100}`, borderRadius: 99, padding: "4px 11px", fontWeight: 500, fontFamily: T.sans }}>
              <Icon name="CheckCircle" size={12} color={T.g500} strokeWidth={2.2} />
              {c}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── FAQ item ── */
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${T.n100}` }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", textAlign: "left", background: "none", border: "none",
        padding: "22px 0", display: "flex", justifyContent: "space-between",
        alignItems: "center", cursor: "pointer", gap: 20, fontFamily: T.sans,
      }}>
        <span style={{ fontSize: 15, fontWeight: 400, color: T.n800, lineHeight: 1.5, letterSpacing: "-0.01em" }}>{q}</span>
        <span style={{
          width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
          border: `1px solid ${open ? T.g500 : T.n200}`,
          background: open ? T.g500 : "transparent",
          color: open ? T.white : T.n400,
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all .22s cubic-bezier(.4,0,.2,1)",
          transform: open ? "rotate(45deg)" : "none",
          fontSize: 18, fontWeight: 300, lineHeight: 1,
        }}>+</span>
      </button>
      <div style={{ overflow: "hidden", maxHeight: open ? 500 : 0, transition: "max-height .32s cubic-bezier(.4,0,.2,1)" }}>
        <p style={{ margin: "0 0 22px", fontSize: 14, color: T.n600, lineHeight: 1.8, paddingRight: 48, fontFamily: T.sans }}>{a}</p>
      </div>
    </div>
  );
}

/* ── Sticky CTA ── */
function StickyBar({ onSignUp }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 500);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 900,
      background: T.white, borderTop: `1px solid ${T.n150}`,
      padding: "14px 20px", display: "flex", alignItems: "center",
      justifyContent: "space-between", gap: 12,
      boxShadow: "0 -4px 24px rgba(0,0,0,.08)",
      transform: visible ? "translateY(0)" : "translateY(100%)",
      transition: "transform .3s cubic-bezier(.4,0,.2,1)",
    }}>
      <div style={{ fontFamily: T.sans }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.n900 }}>Ready to run your office maintenance properly?</div>
        <div style={{ fontSize: 12, color: T.n400 }}>14-day free trial · No credit card required</div>
      </div>
      <button onClick={onSignUp} style={{
        padding: "11px 22px", background: T.g500, color: T.white,
        border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600,
        fontFamily: T.sans, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
      }}
        onMouseEnter={e => e.currentTarget.style.background = T.g600}
        onMouseLeave={e => e.currentTarget.style.background = T.g500}>
        Start free trial
      </button>
    </div>
  );
}

/* ══ MAIN PAGE ══ */
export default function CorporateServicesPage() {
  useFonts();
  const navigate = useNavigate();
  const [activePersona, setActivePersona] = useState(0);

  const goToSignUp   = () => navigate("/signup?type=corporate");
  const goToContact  = () => navigate("/contact?subject=corporate");
  const goToBusiness = () => navigate("/business");

  return (
    <div style={{ fontFamily: T.sans, background: T.white, minHeight: "100vh", color: T.n900, paddingBottom: 80 }}>
      <style>{`* { box-sizing: border-box; } ::-webkit-scrollbar { display: none; }`}</style>

      {/* ── Nav ── */}
      <div style={{ padding: "16px 24px", borderBottom: `1px solid ${T.n100}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button onClick={() => navigate(-1)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0 }}
          onMouseEnter={e => e.currentTarget.style.color = T.n800}
          onMouseLeave={e => e.currentTarget.style.color = T.n400}>
          <Icon name="ArrowLeft" size={14} strokeWidth={2} />
          Back
        </button>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={goToBusiness} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: `1px solid ${T.n200}`, cursor: "pointer", color: T.n600, fontSize: 13, fontFamily: T.sans, padding: "8px 14px", borderRadius: 8, fontWeight: 500 }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = T.n400; e.currentTarget.style.color = T.n900; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.n200; e.currentTarget.style.color = T.n600; }}>
            See all business plans
          </button>
          <button onClick={goToSignUp} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: T.g500, border: "none", cursor: "pointer", color: T.white, fontSize: 13, fontFamily: T.sans, padding: "8px 16px", borderRadius: 8, fontWeight: 600 }}
            onMouseEnter={e => e.currentTarget.style.background = T.g600}
            onMouseLeave={e => e.currentTarget.style.background = T.g500}>
            Start free trial
          </button>
        </div>
      </div>

      {/* ══ HERO ══ */}
      <div style={{ padding: "clamp(56px,8vw,100px) 20px clamp(48px,6vw,80px)", borderBottom: `1px solid ${T.n100}` }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: T.g600, fontSize: 12, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 24 }}>
            <Icon name="Building2" size={14} color={T.g500} strokeWidth={2.2} />
            Corporate services
          </div>
          <h1 style={{ fontFamily: T.serif, fontSize: "clamp(40px,7.5vw,68px)", fontWeight: 400, margin: "0 0 22px", lineHeight: 1.05, color: T.n900, letterSpacing: "-0.03em", maxWidth: 680 }}>
            Your office, running the way<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>it should always have.</em>
          </h1>
          <p style={{ fontSize: "clamp(15px,2vw,17px)", color: T.n600, lineHeight: 1.75, margin: "0 0 16px", maxWidth: 540 }}>
            JiraniApp gives corporate teams a single platform to book, track, and pay for every facility service. Cleaning, electrical, security, landscaping, moving, and maintenance, all managed by your team and billed on one invoice.
          </p>
          <p style={{ fontSize: 14, color: T.n400, lineHeight: 1.65, margin: "0 0 36px", maxWidth: 500 }}>
            For facilities managers who need things running. Finance leads who need clean records. And directors who need to stop thinking about it entirely.
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <button onClick={goToSignUp} style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: T.g500, color: T.white, border: "none",
              padding: "15px 30px", borderRadius: 11, fontSize: 15, fontWeight: 600,
              cursor: "pointer", fontFamily: T.sans, letterSpacing: "0.01em",
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              Start your free trial
              <Icon name="ArrowRight" size={16} color={T.white} strokeWidth={2} />
            </button>
            <button onClick={goToContact} style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "none", border: `1.5px solid ${T.n200}`, padding: "14px 24px", borderRadius: 11, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: T.sans, color: T.n700 }}
              onMouseEnter={e => e.currentTarget.style.borderColor = T.n400}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.n200}>
              Book a demo
            </button>
          </div>
        </div>
      </div>

      {/* ══ TRUST STRIP ══ */}
      <div style={{ borderBottom: `1px solid ${T.n100}`, padding: "0 20px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}>
          {[
            { icon: "BadgeCheck",      label: "Business-tier verified JiraniPros only" },
            { icon: "Repeat",          label: "Recurring schedules that run automatically" },
            { icon: "FileText",        label: "One monthly invoice for your accounts team" },
            { icon: "ShieldCheck",     label: "NDA-ready professionals on request" },
          ].map(({ icon, label }, i) => (
            <div key={i} style={{ padding: "20px 14px", display: "flex", alignItems: "center", gap: 10, borderRight: i < 3 ? `1px solid ${T.n100}` : "none" }}>
              <Icon name={icon} size={16} color={T.g500} strokeWidth={1.6} />
              <span style={{ fontSize: 12, color: T.n600, lineHeight: 1.4 }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 20px" }}>

        {/* ══ PERSONA SWITCHER ══ */}
        <div style={{ paddingTop: "clamp(48px,7vw,72px)", marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Built for your whole team</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            What JiraniApp means for <em style={{ fontStyle: "italic", color: T.g600 }}>your role</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 28px", lineHeight: 1.65 }}>
            The right product answers different questions for different people. Select your role below.
          </p>

          {/* Tabs */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 28 }}>
            {PERSONAS.map((p, i) => (
              <PersonaCard key={i} persona={p} active={activePersona === i} onClick={() => setActivePersona(i)} />
            ))}
          </div>

          {/* Active persona content */}
          <div style={{ padding: "clamp(24px,4vw,36px)", borderRadius: 18, border: `1.5px solid ${T.g100}`, background: T.g50, transition: "all .2s" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>{PERSONAS[activePersona].role}</div>
            <h3 style={{ fontFamily: T.serif, fontSize: "clamp(20px,3.5vw,28px)", fontWeight: 400, color: T.n900, margin: "0 0 20px", letterSpacing: "-0.025em", lineHeight: 1.2 }}>
              {PERSONAS[activePersona].headline}
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {PERSONAS[activePersona].points.map((pt, i) => (
                <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "12px 14px", borderRadius: 10, background: T.white, border: `1px solid ${T.g100}` }}>
                  <div style={{ flexShrink: 0, marginTop: 1 }}>
                    <Icon name="CheckCircle" size={15} color={T.g500} strokeWidth={2} />
                  </div>
                  <span style={{ fontSize: 14, color: T.n700, lineHeight: 1.55 }}>{pt}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ══ STATS ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
          {STATS.map((s, i) => (
            <div key={i} style={{ padding: "24px", borderRadius: 16, border: `1px solid ${T.n150}`, background: T.white }}>
              <div style={{ fontFamily: T.serif, fontSize: "clamp(28px,5vw,40px)", fontWeight: 400, color: T.g500, letterSpacing: "-0.03em", lineHeight: 1, marginBottom: 10 }}>{s.value}</div>
              <p style={{ fontSize: 13, color: T.n600, lineHeight: 1.65, margin: 0 }}>{s.label}</p>
            </div>
          ))}
        </div>

        {/* ══ SERVICES ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Services available</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Six service categories, <em style={{ fontStyle: "italic", color: T.g600 }}>one account</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 28px", lineHeight: 1.65 }}>
            Book any combination of services under your corporate account. Mix recurring schedules with on-demand bookings across all six categories. Everything is tracked, documented, and invoiced together.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
            {SERVICES.map((s, i) => <ServiceCard key={i} {...s} />)}
          </div>
        </div>

        {/* ══ HOW IT WORKS ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Getting started</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Set up and running in <em style={{ fontStyle: "italic", color: T.g600 }}>under a day</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 36px", lineHeight: 1.65 }}>
            Most corporate accounts complete onboarding, add their team, and schedule their first recurring jobs on the same day they sign up.
          </p>
          <div style={{ paddingLeft: 4 }}>
            {HOW_IT_WORKS.map((step, i) => (
              <WorkStep key={i} step={step} isLast={i === HOW_IT_WORKS.length - 1} />
            ))}
          </div>
          <div style={{ marginTop: 36, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <button onClick={goToSignUp} style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: T.g500, color: T.white, border: "none",
              padding: "13px 26px", borderRadius: 10, fontSize: 14, fontWeight: 600,
              cursor: "pointer", fontFamily: T.sans,
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              Start your free trial
              <Icon name="ArrowRight" size={15} color={T.white} strokeWidth={2} />
            </button>
            <span style={{ fontSize: 13, color: T.n400 }}>14 days free, no card required</span>
          </div>
        </div>

        {/* ══ AUDIT AND COMPLIANCE BLOCK ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)", padding: "clamp(28px,5vw,44px)", borderRadius: 20, background: T.n50, border: `1px solid ${T.n150}` }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, color: T.n600, fontSize: 11, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 14 }}>
            <Icon name="Lock" size={13} color={T.n400} strokeWidth={2} />
            Records and compliance
          </div>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(22px,4vw,30px)", fontWeight: 400, color: T.n900, margin: "0 0 10px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            Every job documented. <em style={{ fontStyle: "italic", color: T.g600 }}>Every invoice audit-ready.</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n600, lineHeight: 1.75, margin: "0 0 28px" }}>
            Corporate environments need more than a receipt. Every booking on JiraniApp generates a full job record including the verified Pro's identity, arrival and completion timestamps, job description, and post-completion photos. Your finance team and auditors have everything they need without asking for it.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
            {[
              { icon: "Clock",      title: "Timestamped job records", desc: "Exact arrival and completion times recorded automatically for every job, every visit." },
              { icon: "Award",      title: "Verified Pro identity on file", desc: "Every job record carries the JiraniPro's name and verified national ID number." },
              { icon: "Camera",     title: "Before and after photo log", desc: "JiraniPros upload photo documentation on completion, stored permanently on your dashboard." },
              { icon: "FileText",   title: "PDF export for accounts", desc: "Any job record or full monthly activity log can be downloaded as a formatted PDF instantly." },
              { icon: "CreditCard", title: "Itemised monthly invoicing", desc: "One invoice, every line itemised by date, location, service, JiraniPro, and amount. VAT-ready." },
              { icon: "Key",        title: "NDA-ready professionals", desc: "JiraniPros for sensitive roles can sign NDAs before attending site, arranged through your account manager." },
            ].map((item, i) => (
              <div key={i} style={{ padding: "16px", borderRadius: 12, background: T.white, border: `1px solid ${T.n150}`, display: "flex", flexDirection: "column", gap: 9 }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: T.n50, border: `1px solid ${T.n150}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name={item.icon} size={15} color={T.n600} strokeWidth={1.7} />
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.n900, letterSpacing: "-0.01em" }}>{item.title}</div>
                <p style={{ fontSize: 12, color: T.n500, lineHeight: 1.65, margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ══ FAQ ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Questions</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 4px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            What corporate clients <em style={{ fontStyle: "italic", color: T.g600 }}>ask us</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 8px", lineHeight: 1.65 }}>
            Practical questions from facilities managers, finance leads, and operations teams.
          </p>
          <div>
            {FAQS.map((item, i) => <FaqItem key={i} {...item} />)}
          </div>
        </div>

        {/* ══ FOOTER CTA ══ */}
        <div style={{ padding: "clamp(36px,5vw,56px)", borderRadius: 20, border: `1px solid ${T.n150}`, textAlign: "center", background: T.n50, marginBottom: 20 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: T.g50, border: `1.5px solid ${T.g200}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
            <Icon name="Briefcase" size={22} color={T.g500} strokeWidth={1.6} />
          </div>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(28px,4.5vw,40px)", fontWeight: 400, color: T.n900, margin: "0 0 12px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Your office deserves<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>a system that works</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 auto 32px", lineHeight: 1.75, maxWidth: 420 }}>
            Start your free 14-day trial. Add your team, schedule your first recurring services, and see what it feels like when facility management stops being a distraction.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={goToSignUp} style={{
              padding: "14px 32px", background: T.g500, color: T.white,
              border: "none", borderRadius: 11, fontSize: 15, fontWeight: 600,
              fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em",
              display: "inline-flex", alignItems: "center", gap: 8,
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              Start free trial
              <Icon name="ArrowRight" size={15} color={T.white} strokeWidth={2} />
            </button>
            <button onClick={goToContact} style={{
              padding: "14px 28px", background: "transparent", color: T.n700,
              border: `1.5px solid ${T.n200}`, borderRadius: 11, fontSize: 15, fontWeight: 600,
              fontFamily: T.sans, cursor: "pointer",
            }}
              onMouseEnter={e => e.currentTarget.style.borderColor = T.n400}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.n200}>
              Book a demo
            </button>
          </div>
          <p style={{ fontSize: 12, color: T.n300, margin: "20px 0 0" }}>
            14-day free trial · No credit card required · Cancel any time
          </p>
        </div>

      </div>

      <StickyBar onSignUp={goToSignUp} />
    </div>
  );
}